/**
 * CopilotTray — Phase-driven AI co-pilot sidebar
 *
 * Lives on the right edge of the phase body. Shows on all phase tabs
 * (not on executive summary). Persists open/close state in localStorage.
 *
 * Design:
 *   1. HEADER: Title "L4E Co-Pilot" + mode badge + collapse button
 *   2. PROGRESS RAIL: Horizontal row of 7 numbered dots/pills
 *   3. NEXT STEP CARD: Green-themed card showing next action
 *   4. CURRENT PHASE SECTION: Phase name + progress bar + subphases
 *   5. TRIAGE Q&A: Embedded inside current phase when seeded (signal_detection only)
 *   6. HISTORY DRAWER: Toggle to show completed phases
 *   7. UPCOMING SECTION: Dimmed future phases
 *   8. WIZARD MODAL: AiSessionWizard overlay
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiWorkspaceAi } from "../../api/useApiWorkspaceAi";
import { fetchAllSecurityUsers, SecurityUserResponse } from "../../api/security";
import {
  TriageEntryResponse,
  fetchTriageEntries,
  seedTriage,
  answerTriageEntry,
  confirmTriageEntry,
  addTriageEntry,
} from "../../api/triage";
import type { WorkspaceResponse } from "../../api/workspaces";
import type {
  ClarifyingQuestionResponse,
} from "../../api/workspaceAi";
import {
  computePhases,
  findNextStep,
  type PhaseContext,
} from "./copilotPhaseConfig";
import AiSessionWizard from "./AiSessionWizard";
import l4eLogo from "../../assets/l4e-copilot-icon.svg";
import styles from "./CopilotTray.module.css";

const TRAY_STATE_KEY = "l4e_copilot_tray_expanded";
const TRIAGE_COLLAPSED_KEY = "l4e_triage_collapsed";
const TRAY_WIDTH_KEY = "l4e_copilot_tray_width";
const TRAY_MIN_WIDTH = 320;
const TRAY_MAX_WIDTH = 640;

interface Props {
  workspace: WorkspaceResponse;
  onRefreshWorkspace: () => void;
  /** When true, the AI backend is live (not simulation). Logo rotates. */
  isLive?: boolean;
}

export const CopilotTray: React.FC<Props> = ({
  workspace,
  onRefreshWorkspace,
  isLive = false,
}) => {
  const { t } = useAppTranslation("copilot");
  const { t: tPages } = useAppTranslation("pages");

  const {
    sessions,
    evidenceItems,
    unansweredQuestions,
    loading,
    error,
    refetch,
  } = useApiWorkspaceAi(workspace.workspaceGuid);

  // Persist tray open/close in localStorage
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(TRAY_STATE_KEY);
      return stored === "true";
    } catch {
      return false;
    }
  });

  // ── Resize state ──
  const [trayWidth, setTrayWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(TRAY_WIDTH_KEY);
      if (stored) {
        const w = parseInt(stored, 10);
        if (!isNaN(w) && w >= TRAY_MIN_WIDTH && w <= TRAY_MAX_WIDTH) return w;
      }
    } catch {}
    return TRAY_MIN_WIDTH;
  });

  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(TRAY_MIN_WIDTH);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = trayWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [trayWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.min(
        TRAY_MAX_WIDTH,
        Math.max(TRAY_MIN_WIDTH, dragStartWidth.current + delta)
      );
      setTrayWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setTrayWidth((w) => {
        try {
          localStorage.setItem(TRAY_WIDTH_KEY, String(w));
        } catch {}
        return w;
      });
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // ── Triage Q&A state ──
  const [triageEntries, setTriageEntries] = useState<TriageEntryResponse[]>([]);
  const [_triageLoading, setTriageLoading] = useState(true);
  const [triageSeeding, setTriageSeeding] = useState(false);
  // triageCollapsed removed — triage now embedded in phase subphase list
  const [triageEditGuid, setTriageEditGuid] = useState<string | null>(null);
  const [triageEditText, setTriageEditText] = useState("");
  const [triageSaving, setTriageSaving] = useState(false);
  const [triageNewQ, setTriageNewQ] = useState("");
  const [triageAddingQ, setTriageAddingQ] = useState(false);
  const [showTriageNewQ, setShowTriageNewQ] = useState(false);
  const [round1Collapsed, setRound1Collapsed] = useState(false);
  const [round2Collapsed, setRound2Collapsed] = useState(false);

  // ── Wizard state ──
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeSessionGuid, setActiveSessionGuid] = useState<string | null>(
    null
  );
  const [wizardSessionType, setWizardSessionType] = useState<
    string | undefined
  >(undefined);

  // ── Phase expand/collapse state ──
  const [expandedPhase, setExpandedPhase] = useState<string | null>(null);
  const [currentPhaseExpanded, setCurrentPhaseExpanded] = useState(true);

  // Load triage entries
  const loadTriageEntries = useCallback(async () => {
    setTriageLoading(true);
    try {
      const data = await fetchTriageEntries(workspace.workspaceGuid);
      setTriageEntries(data);
    } catch {
      /* empty state will show */
    } finally {
      setTriageLoading(false);
    }
  }, [workspace.workspaceGuid]);

  useEffect(() => {
    loadTriageEntries();
  }, [loadTriageEntries]);

  // ── Triage handlers ──
  const handleTriageSeed = useCallback(async () => {
    setTriageSeeding(true);
    try {
      const data = await seedTriage(workspace.workspaceGuid, {
        concernCategory: workspace.concernCategory ?? undefined,
      });
      setTriageEntries(data);
    } catch {
      /* silent */
    } finally {
      setTriageSeeding(false);
    }
  }, [workspace.workspaceGuid, workspace.concernCategory]);

  const handleTriageAnswer = useCallback(
    async (entryGuid: string) => {
      if (!triageEditText.trim()) return;
      setTriageSaving(true);
      try {
        const updated = await answerTriageEntry(
          workspace.workspaceGuid,
          entryGuid,
          {
            answerText: triageEditText.trim(),
          }
        );
        setTriageEntries((prev) =>
          prev.map((e) => (e.entryGuid === entryGuid ? updated : e))
        );
        setTriageEditGuid(null);
        setTriageEditText("");
      } catch {
        /* silent */
      } finally {
        setTriageSaving(false);
      }
    },
    [workspace.workspaceGuid, triageEditText]
  );

  const handleTriageConfirm = useCallback(
    async (entryGuid: string) => {
      try {
        const updated = await confirmTriageEntry(
          workspace.workspaceGuid,
          entryGuid,
          { status: "confirmed" }
        );
        setTriageEntries((prev) =>
          prev.map((e) => (e.entryGuid === entryGuid ? updated : e))
        );
      } catch {
        /* silent */
      }
    },
    [workspace.workspaceGuid]
  );

  const handleTriageCorrect = useCallback(
    async (entryGuid: string) => {
      if (!triageEditText.trim()) return;
      setTriageSaving(true);
      try {
        const updated = await confirmTriageEntry(
          workspace.workspaceGuid,
          entryGuid,
          {
            status: "corrected",
            correctedAnswer: triageEditText.trim(),
          }
        );
        setTriageEntries((prev) =>
          prev.map((e) => (e.entryGuid === entryGuid ? updated : e))
        );
        setTriageEditGuid(null);
        setTriageEditText("");
      } catch {
        /* silent */
      } finally {
        setTriageSaving(false);
      }
    },
    [workspace.workspaceGuid, triageEditText]
  );

  const handleTriageAddQuestion = useCallback(async () => {
    if (!triageNewQ.trim()) return;
    setTriageAddingQ(true);
    try {
      const entry = await addTriageEntry(workspace.workspaceGuid, {
        questionText: triageNewQ.trim(),
        entryType: "follow_up",
      });
      setTriageEntries((prev) => [...prev, entry]);
      setTriageNewQ("");
      setShowTriageNewQ(false);
    } catch {
      /* silent */
    } finally {
      setTriageAddingQ(false);
    }
  }, [workspace.workspaceGuid, triageNewQ]);

  // ── Triage categorization ──
  const round1Entries = useMemo(
    () => triageEntries.filter((e) => !!e.triageQuestionGuid),
    [triageEntries]
  );
  const round2Entries = useMemo(
    () => triageEntries.filter((e) => !e.triageQuestionGuid),
    [triageEntries]
  );

  // ── Phase computation ──
  const phaseContext: PhaseContext = useMemo(
    () => ({
      workspace,
      sessions,
      triageEntries,
      evidenceItems,
    }),
    [workspace, sessions, triageEntries, evidenceItems]
  );

  const computedPhases = useMemo(
    () => computePhases(phaseContext),
    [phaseContext]
  );

  const nextStep = useMemo(
    () => findNextStep(computedPhases),
    [computedPhases]
  );

  const currentPhase = useMemo(
    () => computedPhases.find((p) => p.status === "in_progress"),
    [computedPhases]
  );

  const completedPhases = useMemo(
    () => computedPhases.filter((p) => p.status === "completed"),
    [computedPhases]
  );

  // Resolve user display names (will be used when session metadata is shown in expanded phases)
  const [_userMap, setUserMap] = useState<Map<string, SecurityUserResponse>>(
    new Map()
  );
  const usersFetched = useRef(false);
  useEffect(() => {
    if (usersFetched.current) return;
    usersFetched.current = true;
    fetchAllSecurityUsers()
      .then((users) => {
        const map = new Map<string, SecurityUserResponse>();
        users.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
        setUserMap(map);
      })
      .catch(() => {
        /* silent */
      });
  }, []);

  // Format date for display
  const formatSessionDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }) +
      " " +
      d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  // Wizard state
  const handleWizardClose = useCallback(() => {
    setWizardOpen(false);
    setActiveSessionGuid(null);
    setWizardSessionType(undefined);
    refetch();
    onRefreshWorkspace();
    loadTriageEntries();
  }, [refetch, onRefreshWorkspace, loadTriageEntries]);

  const handleNextStepClick = useCallback(() => {
    if (!nextStep) return;

    if (nextStep.type === "ai") {
      setActiveSessionGuid(null);
      setWizardSessionType(nextStep.sessionType);
      setWizardOpen(true);
    } else if (nextStep.type === "manual" && nextStep.key === "triage_seed") {
      handleTriageSeed();
    } else if (nextStep.type === "manual") {
      // For other manual steps, open wizard with "scroll_triage" action
      // (or just do nothing if not actionable)
    } else if (nextStep.type === "gate") {
      // Gates require manual validation, not actionable from button
    }
  }, [nextStep, handleTriageSeed]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(TRAY_STATE_KEY, String(next));
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  }, []);

  // Render triage entry
  const renderTriageEntry = (entry: TriageEntryResponse, idx: number) => {
    const isPrefill = entry.entryType === "question_prefill";
    const isEditing = triageEditGuid === entry.entryGuid;
    const hasAnswer = !!entry.answerText;
    const isConfirmed = entry.confirmationStatus === "confirmed";
    const isCorrected = entry.confirmationStatus === "corrected";

    return (
      <div key={entry.entryGuid} className={styles.triageEntry}>
        <div className={styles.triageQuestion}>
          <span className={styles.triageNum}>{idx + 1}</span>
          <span className={styles.triageQText}>{entry.questionText}</span>
        </div>
        {isPrefill && !hasAnswer && (
          <span className={styles.triageBadgePrefill}>
            {tPages("workspace.triage.prefilled")}
          </span>
        )}
        {isConfirmed && (
          <span className={styles.triageBadgeConfirmed}>
            {tPages("workspace.triage.confirmed")}
          </span>
        )}
        {isCorrected && (
          <span className={styles.triageBadgeCorrected}>
            {tPages("workspace.triage.corrected")}
          </span>
        )}
        {hasAnswer && !isEditing && (
          <div className={styles.triageAnswer}>{entry.answerText}</div>
        )}
        {!isEditing && (
          <div className={styles.triageActions}>
            {isPrefill && !hasAnswer && (
              <>
                <button
                  type="button"
                  className={styles.triageConfirmBtn}
                  onClick={() => handleTriageConfirm(entry.entryGuid)}
                >
                  {tPages("workspace.triage.confirmBtn")}
                </button>
                <button
                  type="button"
                  className={styles.triageCorrectBtn}
                  onClick={() => {
                    setTriageEditGuid(entry.entryGuid);
                    setTriageEditText("");
                  }}
                >
                  {tPages("workspace.triage.correctBtn")}
                </button>
              </>
            )}
            {!hasAnswer && !isPrefill && (
              <button
                type="button"
                className={styles.triageEditBtn}
                onClick={() => {
                  setTriageEditGuid(entry.entryGuid);
                  setTriageEditText("");
                }}
              >
                {tPages("workspace.triage.editBtn")}
              </button>
            )}
            {hasAnswer && !isPrefill && (
              <button
                type="button"
                className={styles.triageEditBtn}
                onClick={() => {
                  setTriageEditGuid(entry.entryGuid);
                  setTriageEditText(entry.answerText ?? "");
                }}
              >
                {tPages("workspace.triage.editBtn")}
              </button>
            )}
          </div>
        )}
        {isEditing && (
          <div className={styles.triageEditForm}>
            <textarea
              className={styles.triageEditInput}
              rows={2}
              value={triageEditText}
              onChange={(e) => setTriageEditText(e.target.value)}
              placeholder={tPages("workspace.triage.answerPlaceholder")}
              disabled={triageSaving}
              autoFocus
            />
            <div className={styles.triageEditActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => {
                  setTriageEditGuid(null);
                  setTriageEditText("");
                }}
                disabled={triageSaving}
              >
                {tPages("workspace.triage.cancelBtn")}
              </button>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={() =>
                  isPrefill
                    ? handleTriageCorrect(entry.entryGuid)
                    : handleTriageAnswer(entry.entryGuid)
                }
                disabled={triageSaving || !triageEditText.trim()}
              >
                {triageSaving
                  ? t("submitting")
                  : isPrefill
                  ? tPages("workspace.triage.submitCorrection")
                  : tPages("workspace.triage.submitAnswer")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Count sessions for a given session type (for showing run count on AI subphases)
  const sessionCountFor = useCallback(
    (sessionType?: string) => {
      if (!sessionType) return 0;
      return sessions.filter(
        (s) => s.sessionType === sessionType && (s.status === "completed" || s.status === "reviewed")
      ).length;
    },
    [sessions]
  );

  // Render subphase row
  const renderSubphaseRow = (subphaseWithStatus: any, stepNum: number) => {
    const { status, type, labelKey, sessionType, key: subKey } = subphaseWithStatus;
    const isReady = status === "ready";
    const isDone = status === "completed";
    const isLocked = status === "locked";

    // Session count for AI subphases (shows how many times this session type has been run)
    const runCount = type === "ai" ? sessionCountFor(sessionType) : 0;

    // Find the latest session for completed AI subphases (to view on click)
    const latestSessionForType = isDone && type === "ai" && sessionType
      ? sessions.find(
          (s) => s.sessionType === sessionType && (s.status === "completed" || s.status === "reviewed")
        )
      : null;

    // Click handler: ready AI subphases open wizard; completed AI subphases view latest session
    const handleClick = isReady && type === "ai" ? () => {
      setActiveSessionGuid(null);
      setWizardSessionType(sessionType);
      setWizardOpen(true);
    } : isReady && subKey === "triage_seed" ? () => {
      handleTriageSeed();
    } : latestSessionForType ? () => {
      setActiveSessionGuid(latestSessionForType.sessionGuid);
      setWizardOpen(true);
    } : undefined;

    // Status label and color — outlined text style (not filled, so they don't compete with the Go button)
    const statusLabel = isDone ? t("status.done") : isReady ? t("status.ready") : isLocked ? t("status.locked") : t("status.pending");
    const pillBg = "transparent";
    const pillColor = isDone ? "#2563eb" : isReady ? "var(--color-success)" : "var(--text-muted)";
    const pillBorder = isDone ? "1px solid rgba(37, 99, 235, 0.4)" : isReady ? "1px solid rgba(34, 197, 94, 0.4)" : "1px solid var(--border-default)";

    // Number circle colors
    const numBg = isDone ? "#2563eb" : isReady ? "var(--color-success)" : "var(--bg-tertiary)";
    const numColor = isDone || isReady ? "var(--text-on-accent)" : "var(--text-secondary)";

    return (
      <div
        key={subKey}
        role={handleClick ? "button" : undefined}
        tabIndex={handleClick ? 0 : undefined}
        onClick={handleClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "7px 10px",
          borderRadius: "6px",
          border: `1px solid ${isDone ? "var(--accent-border)" : isReady ? "var(--color-success)" : "var(--border-default)"}`,
          background: isDone ? "var(--accent-bg)" : isReady ? "rgba(34, 197, 94, 0.08)" : "transparent",
          opacity: isLocked ? 0.45 : 1,
          cursor: handleClick ? "pointer" : "default",
          marginBlockEnd: "4px",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        {/* Step number — larger, higher contrast */}
        <span style={{
          width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "11px", fontWeight: 700,
          background: numBg, color: numColor,
        }}>
          {stepNum}
        </span>
        {/* Type icon: L4E logo for AI, gate/manual icons for others */}
        {type === "ai" ? (
          <img src={l4eLogo} alt="" style={{ width: "16px", height: "16px", flexShrink: 0 }} />
        ) : (
          <span style={{ fontSize: "14px", flexShrink: 0, lineHeight: 1 }}>
            {type === "gate" ? "\uD83D\uDEE1\uFE0F" : "\u{1F464}"}
          </span>
        )}
        {/* Label */}
        <span style={{ flex: 1, fontSize: "11px", fontWeight: 500, color: "var(--text-primary)" }}>
          {t(labelKey)}
        </span>
        {/* Run count badge for AI subphases with multiple sessions */}
        {runCount > 1 && (
          <span style={{
            fontSize: "9px", fontWeight: 700,
            padding: "1px 5px", borderRadius: "8px",
            background: "var(--bg-tertiary)", color: "var(--text-muted)",
          }}>
            {runCount}x
          </span>
        )}
        {/* Status badge — fixed width for alignment */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "52px",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "9px",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textAlign: "center",
            backgroundColor: pillBg,
            color: pillColor,
            border: pillBorder,
            flexShrink: 0,
          }}
        >
          {statusLabel}
        </span>
      </div>
    );
  };

  // Auto-expand on first load
  useEffect(() => {
    if (!loading) {
      const hasStoredPref = localStorage.getItem(TRAY_STATE_KEY) !== null;
      if (!hasStoredPref && (unansweredQuestions.length > 0 || sessions.length === 0)) {
        setExpanded(true);
      }
    }
  }, [loading, unansweredQuestions.length, sessions.length]);

  return (
    <>
      <div
        className={`${styles.tray} ${
          expanded ? styles.trayExpanded : styles.trayCollapsed
        }`}
        style={expanded ? { width: trayWidth } : undefined}
      >
        {/* ── Resize handle (visible when expanded) ── */}
        {expanded && (
          <div
            className={`${styles.resizeHandle} ${
              isDragging.current ? styles.resizeHandleActive : ""
            }`}
            onMouseDown={handleResizeStart}
          />
        )}

        {/* ── Collapsed strip ── */}
        {!expanded && (
          <button
            type="button"
            className={styles.collapsedTab}
            onClick={toggleExpanded}
            aria-label={t("tray.expand")}
            title={t("tray.expand")}
          >
            <svg
              className={styles.collapsedChevron}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <img
              src={l4eLogo}
              alt=""
              className={`${styles.logoCollapsed} ${
                isLive ? styles.logoSpin : ""
              }`}
            />
            <span className={styles.collapsedLabel}>{t("tray.title")}</span>
          </button>
        )}

        {/* ── Expanded panel ── */}
        {expanded && (
          <div className={styles.panelContent}>
            {/* Header */}
            <div className={styles.panelHeader}>
              <img
                src={l4eLogo}
                alt=""
                className={`${styles.logoExpanded} ${
                  isLive ? styles.logoSpin : ""
                }`}
              />
              <span className={styles.panelTitle}>{t("tray.title")}</span>
              <span className={styles.modeBadge}>
                {isLive ? t("liveMode") : t("simulationMode")}
              </span>
              <button
                type="button"
                className={styles.collapseBtn}
                onClick={toggleExpanded}
                aria-label={t("tray.collapse")}
                title={t("tray.collapse")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>

            {loading && (
              <div className={styles.loadingText}>{t("loading")}</div>
            )}

            {error && (
              <div className={styles.errorText}>
                {error}
                <button
                  type="button"
                  className={styles.retryLink}
                  onClick={refetch}
                >
                  {t("retry")}
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Current Phase — Option C: integrated green block with Next Step inside */}
                {currentPhase && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {/* "NEXT STEP" label above the green block */}
                    {nextStep && (
                      <div style={{
                        fontSize: "10px", fontWeight: 700, color: "var(--text-tertiary)",
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        padding: "0 2px 6px",
                      }}>
                        {t("tray.nextStep")}
                      </div>
                    )}

                    {/* Green block: phase name row + action row */}
                    <div style={{ background: "#16a34a", borderRadius: "0" }}>
                      {/* Phase name row (clickable to expand/collapse) */}
                      <button
                        type="button"
                        onClick={() => setCurrentPhaseExpanded((p) => !p)}
                        style={{
                          width: "100%",
                          background: "transparent",
                          padding: "10px 12px",
                          border: "none",
                          borderBlockEnd: "1px solid rgba(255, 255, 255, 0.15)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "var(--text-on-accent)",
                          fontSize: "15px",
                          fontWeight: 600,
                          cursor: "pointer",
                          textAlign: "start",
                        }}>
                        <span style={{
                          fontSize: "10px", opacity: 0.7,
                          transform: currentPhaseExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.15s",
                          display: "inline-block",
                        }}>&#x25B6;</span>
                        <span style={{ flex: 1 }}>
                          {currentPhase.num}: {tPages(currentPhase.labelKey)}
                        </span>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          minWidth: "52px", padding: "2px 6px",
                          background: "rgba(255, 255, 255, 0.2)", color: "var(--text-on-accent)",
                          borderRadius: "4px", fontSize: "9px", fontWeight: 600, letterSpacing: "0.04em",
                        }}>
                          {t("tray.stepOf", { current: currentPhase.completedCount, total: currentPhase.subphases.length })}
                        </span>
                      </button>

                      {/* Action row inside green: task name + hint + Go button */}
                      {nextStep && (
                        <div style={{
                          padding: "10px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--text-on-accent)" }}>
                              {t(nextStep.labelKey)}
                            </div>
                            <div style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.75)", marginBlockStart: "2px", lineHeight: 1.4 }}>
                              {t(nextStep.hintKey || "subphaseHints.intake")}
                            </div>
                            <div style={{ fontSize: "11px", color: "rgba(255, 255, 255, 0.6)", marginBlockStart: "3px" }}>
                              {t("tray.teamLeadOnly")}
                            </div>
                          </div>
                          <button
                            type="button"
                            style={{
                              padding: "8px 20px",
                              background: "#ffffff",
                              color: "#16a34a",
                              border: "none",
                              borderRadius: "4px",
                              fontWeight: 700,
                              fontSize: "16px",
                              cursor: "pointer",
                              transition: "opacity 0.15s",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                            onClick={handleNextStepClick}
                            disabled={wizardOpen || triageSeeding}
                          >
                            {t("tray.go")}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 3. Collapsible subphases + triage beneath the green bar */}
                    <div style={{
                      maxHeight: currentPhaseExpanded ? "1200px" : "0px",
                      overflow: "hidden",
                      transition: "max-height var(--duration-slow) ease-in-out",
                      background: "var(--bg-primary)",
                    }}>

                    {/* Subphases — natural order (1,2,3) for active work */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "8px 10px" }}>
                      {currentPhase.subphaseStatuses.map((sub, idx) =>
                        renderSubphaseRow(sub, idx + 1)
                      )}
                    </div>

                    {/* Triage Q&A (embedded if signal_detection and seeded) */}
                    {currentPhase.key === "signal_detection" &&
                      triageEntries.length > 0 && (
                        <div
                          style={{
                            marginTop: "8px",
                            paddingTop: "8px",
                            borderTop: "1px solid var(--border-default)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                color: "var(--text-tertiary)",
                              }}
                            >
                              {tPages("workspace.triage.threadTitle")}
                            </span>
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                background: "var(--bg-elevated)",
                                borderRadius: "3px",
                                color: "var(--text-tertiary)",
                              }}
                            >
                              {triageEntries.length}
                            </span>
                          </div>

                          {/* Round groups */}
                          {round2Entries.length > 0 && (
                            <div style={{ marginBottom: "8px" }}>
                              <button
                                type="button"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "var(--text-tertiary)",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  marginBottom: "4px",
                                }}
                                onClick={() =>
                                  setRound2Collapsed((p) => !p)
                                }
                              >
                                {round2Collapsed ? "▶" : "▼"} Round 2 (AI
                                Follow-up)
                              </button>
                              {!round2Collapsed && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                  }}
                                >
                                  {round2Entries.map((entry, idx) =>
                                    renderTriageEntry(entry, idx)
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {round1Entries.length > 0 && (
                            <div>
                              <button
                                type="button"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "var(--text-tertiary)",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  marginBottom: "4px",
                                }}
                                onClick={() =>
                                  setRound1Collapsed((p) => !p)
                                }
                              >
                                {round1Collapsed ? "▶" : "▼"} Round 1
                                (Seeded)
                              </button>
                              {!round1Collapsed && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                  }}
                                >
                                  {round1Entries.map((entry, idx) =>
                                    renderTriageEntry(entry, idx)
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Add follow-up question */}
                          <div style={{ marginTop: "8px" }}>
                            {!showTriageNewQ ? (
                              <button
                                type="button"
                                className={styles.triageAddBtn}
                                onClick={() => setShowTriageNewQ(true)}
                              >
                                + {tPages("workspace.triage.addQuestion")}
                              </button>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <textarea
                                  className={styles.triageEditInput}
                                  rows={2}
                                  value={triageNewQ}
                                  onChange={(e) =>
                                    setTriageNewQ(e.target.value)
                                  }
                                  placeholder={tPages(
                                    "workspace.triage.questionPlaceholder"
                                  )}
                                  disabled={triageAddingQ}
                                  autoFocus
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <button
                                    type="button"
                                    className={styles.cancelBtn}
                                    onClick={() => {
                                      setShowTriageNewQ(false);
                                      setTriageNewQ("");
                                    }}
                                    disabled={triageAddingQ}
                                  >
                                    {tPages("workspace.triage.cancelBtn")}
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.submitBtn}
                                    onClick={handleTriageAddQuestion}
                                    disabled={
                                      !triageNewQ.trim() || triageAddingQ
                                    }
                                  >
                                    {triageAddingQ
                                      ? tPages("workspace.triage.adding")
                                      : tPages(
                                          "workspace.triage.submitQuestion"
                                        )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    </div>{/* end collapsible content */}
                  </div>
                )}

                {/* Spacer pushes completed phases to bottom of scrollable area */}
                <div style={{ flex: 1, minHeight: "16px" }} />

                {/* Completed Phases — stacked at bottom, newest on top */}
                {completedPhases.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {[...completedPhases].reverse().map((phase) => (
                      <div
                        key={phase.key}
                        style={{
                          background: "#2563eb",
                          borderRadius: "0",
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            width: "100%",
                            background: "transparent",
                            border: "none",
                            color: "var(--text-on-accent)",
                            fontSize: "12px",
                            fontWeight: 600,
                            textAlign: "start",
                            cursor: "pointer",
                            padding: "12px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onClick={() =>
                            setExpandedPhase(
                              expandedPhase === phase.key ? null : phase.key
                            )
                          }
                        >
                          <span style={{
                            fontSize: "10px", color: "var(--text-on-accent)", opacity: 0.7,
                            transform: expandedPhase === phase.key ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.15s",
                            display: "inline-block",
                          }}>&#x25B6;</span>
                          <span style={{ flex: 1 }}>
                            {phase.num}: {tPages(phase.labelKey)}
                          </span>
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            minWidth: "52px", padding: "2px 6px",
                            background: "rgba(255, 255, 255, 0.2)", color: "var(--text-on-accent)",
                            borderRadius: "4px", fontSize: "9px", fontWeight: 600, letterSpacing: "0.04em",
                          }}>
                            {t("status.completed")}
                          </span>
                        </button>

                        <div style={{
                          maxHeight: expandedPhase === phase.key ? "600px" : "0px",
                          overflow: "hidden",
                          transition: "max-height var(--duration-slow) ease-in-out",
                        }}>
                          <div style={{
                            padding: "10px 12px",
                            background: "var(--bg-primary)",
                            borderBlockStart: "1px solid var(--accent-border)",
                          }}>
                            {phase.completedAt && (
                              <div style={{ fontSize: "10px", color: "var(--text-tertiary)", marginBlockEnd: "8px" }}>
                                {formatSessionDate(phase.completedAt)}
                                {phase.confidence != null && ` \u2022 ${Math.round(phase.confidence * 100)}% confidence`}
                              </div>
                            )}
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              {[...phase.subphaseStatuses].reverse().map((sub) => {
                                const originalIdx = phase.subphaseStatuses.indexOf(sub);
                                return renderSubphaseRow(sub, originalIdx + 1);
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upcoming phases are shown in the progress rail at the top — no need to repeat here */}

                {/* AI Sessions are now shown inline within subphase rows.
                   Multiple runs appear as a count badge on the subphase.
                   Clicking a completed AI subphase opens the most recent session in the wizard. */}

                {/* Unanswered questions */}
                {unansweredQuestions.length > 0 && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitleAlert}>
                      {t("questionsTitle")}
                      <span className={styles.sectionCountAlert}>
                        {unansweredQuestions.length}
                      </span>
                    </div>
                    <div className={styles.questionList}>
                      {unansweredQuestions.map(
                        (q: ClarifyingQuestionResponse, idx: number) => (
                          <div
                            key={q.questionGuid}
                            className={styles.questionItem}
                          >
                            <div className={styles.questionNum}>{idx + 1}</div>
                            <div className={styles.questionBody}>
                              <div className={styles.questionText}>
                                {q.questionText}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Evidence summary */}
                {evidenceItems.length > 0 && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                      {t("evidenceTitle")}
                      <span className={styles.sectionCount}>
                        {evidenceItems.length}
                      </span>
                    </div>
                    <div className={styles.evidenceSummary}>
                      {t("tray.evidenceSummary", {
                        total: evidenceItems.length,
                        collected: evidenceItems.filter(
                          (e) =>
                            e.status === "collected" ||
                            e.status === "analyzed"
                        ).length,
                      })}
                    </div>
                  </div>
                )}
                {/* ── Debug: Phase Simulation (localStorage l4e_debug=true) ── */}
                {(() => {
                  try { if (localStorage.getItem("l4e_debug") !== "true") return null; } catch { return null; }
                  return (
                    <div style={{
                      padding: "10px", marginBlockStart: "8px",
                      background: "rgba(239, 68, 68, 0.08)",
                      border: "1px dashed var(--color-error)",
                      borderRadius: "6px",
                    }}>
                      <div style={{
                        fontSize: "10px", fontWeight: 700, color: "var(--color-error)",
                        textTransform: "uppercase", letterSpacing: "0.05em", marginBlockEnd: "8px",
                      }}>
                        Debug: Phase Simulation
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {computedPhases.map((phase) => (
                          <button
                            key={phase.key}
                            type="button"
                            style={{
                              padding: "6px 10px", borderRadius: "4px", fontSize: "10px",
                              fontWeight: 500, cursor: "pointer", textAlign: "start",
                              border: phase.status === "in_progress"
                                ? "1px solid var(--color-success)"
                                : "1px solid var(--border-default)",
                              background: phase.status === "in_progress"
                                ? "rgba(34, 197, 94, 0.12)"
                                : "transparent",
                              color: "var(--text-primary)",
                            }}
                            onClick={async () => {
                              try {
                                const { transitionWorkspacePhase } = await import("../../api/workspaces");
                                await transitionWorkspacePhase(workspace.workspaceGuid, {
                                  toPhase: phase.key,
                                  rationale: `[DEBUG] Simulated transition to ${phase.key}`,
                                });
                                onRefreshWorkspace();
                                refetch();
                              } catch (err) {
                                console.error("Debug phase transition failed:", err);
                              }
                            }}
                          >
                            {phase.status === "in_progress" ? "\u25C9" : "\u25CB"}{" "}
                            {phase.num}: {tPages(phase.labelKey)}
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: "9px", color: "var(--text-muted)", marginBlockStart: "6px" }}>
                        Set localStorage.setItem(&quot;l4e_debug&quot;, &quot;true&quot;) to show
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Wizard modal overlay ── */}
      {wizardOpen && (
        <div className={styles.modalOverlay} onClick={handleWizardClose}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <AiSessionWizard
              workspace={workspace}
              sessionGuid={activeSessionGuid}
              initialSessionType={wizardSessionType}
              onClose={handleWizardClose}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CopilotTray;
